import { Cell, Grid, HFlow, Icon, InfoLabel, Tag, Text, Theme, Tooltip, useStyles, VFlow } from 'bold-ui'
import { green } from 'bold-ui/lib/styles/colors'
import { AccordionButton } from 'components/accordion/AccordionButton'
import { DateTime } from 'components/date'
import { AccordionField } from 'components/form/field/AccordionField'
import { css } from 'emotion'
import { noop } from 'lodash'
import { CSSProperties } from 'react'
import { AccordionItem, AccordionItemPanel } from 'react-accessible-accordion'

import { origemDadosVacinacao } from '../model'
import { getFabricanteSiglaOuNome } from '../utils-vacinacao'
import { RegistroDoseWrapperProps } from './RegistroDoseWrapper'
import { VacinacaoVisualizacaoButtons } from './VacinacaoVisualizacaoButtons'

export const RegistroAnteriorDoseAccordionItem = (props: RegistroDoseWrapperProps) => {
  const {
    dataRegistro,
    dataAplicacao,
    doseNome,
    doseSigla,
    nomeProfissional,
    cbo,
    lote,
    nomeFabricante,
    observacoes,
    unidadeSaude,
    origemDados,
    registradoAtendimento,
    idUnicoRegistro,
    estrategia,
    isAplicadoExterior,
    isCovid,
    isAplicacaoEquivalente,
    handleOnDelete = noop,
    handleOnEdit = noop,
    readOnly = false,
    nomeImunobiologico,
    siglaFabricante,
  } = props

  const { classes } = useStyles(createStyles)

  const renderRegistroAnteriorDoseContent = () => {
    const registroAnteriorDoseContentProps: RegistroAnteriorDoseContentProps = {
      nomeFabricante,
      siglaFabricante,
      lote,
      observacoes,
      nomeProfissional,
      cbo,
      unidadeSaude,
      dataRegistro,
      doseNome,
      doseSigla,
      estrategia,
      isAplicadoExterior,
      isCovid,
      isAplicacaoEquivalente,
      nomeImunobiologico,
    }
    return <RegistroAnteriorDoseContent {...registroAnteriorDoseContentProps} />
  }

  if (readOnly) {
    return renderRegistroAnteriorDoseContent()
  }

  return (
    <VFlow vSpacing={0}>
      <AccordionField name='Registro' allowZeroExpanded>
        <AccordionItem>
          <AccordionButton style={classes.accordionButton}>
            <HFlow
              alignItems='center'
              justifyContent='space-between'
              style={css`
                padding: 0.5rem;
                min-width: 35rem;
                width: 100%;
              `}
            >
              <HFlow alignItems='center'>
                <Icon icon='angleDown' size={1.5} />
                <Icon color={green.c40} icon='needleFilled' size={1.1} />
                <VFlow vSpacing={0.3}>
                  <HFlow alignItems='center' hSpacing={0.3}>
                    <Text fontWeight='bold'>Dose aplicada em </Text>
                    <DateTime
                      value={dataAplicacao}
                      style={css`
                        font-weight: bold;
                      `}
                      format='DD/MM/YYYY'
                    />
                  </HFlow>
                  {isAplicacaoEquivalente && <Text>{nomeImunobiologico}</Text>}
                </VFlow>
              </HFlow>
              <HFlow hSpacing={0.3} alignItems='center'>
                {isAplicacaoEquivalente && (
                  <Tooltip text='Essa é uma aplicação de uma dose equivalente'>
                    <Tag type='normal'>Equivalente</Tag>
                  </Tooltip>
                )}
                <Tooltip text={origemDadosVacinacao[origemDados]?.hint}>
                  <Tag type='info'>{origemDadosVacinacao[origemDados]?.label}</Tag>
                </Tooltip>
                {registradoAtendimento && !isAplicacaoEquivalente && (
                  <VacinacaoVisualizacaoButtons
                    idUnicoRegistro={idUnicoRegistro}
                    tipoModal='ANTERIOR'
                    handleOnDelete={handleOnDelete}
                    handleOnEdit={handleOnEdit}
                  />
                )}
              </HFlow>
            </HFlow>
          </AccordionButton>
          <AccordionItemPanel className={classes.panel}>{renderRegistroAnteriorDoseContent()}</AccordionItemPanel>
        </AccordionItem>
      </AccordionField>
    </VFlow>
  )
}

interface RegistroAnteriorDoseContentProps {
  dataRegistro: string
  doseSigla?: string
  doseNome?: string
  nomeProfissional: string
  cbo: string
  lote: string
  nomeFabricante: string
  siglaFabricante?: string
  observacoes: string
  unidadeSaude: string
  estrategia: string
  isAplicadoExterior: boolean
  isAplicacaoEquivalente: boolean
  isCovid: boolean
  nomeImunobiologico?: string
}

const RegistroAnteriorDoseContent = (props: RegistroAnteriorDoseContentProps) => {
  const {
    nomeFabricante,
    siglaFabricante,
    lote,
    observacoes,
    nomeProfissional,
    cbo,
    unidadeSaude,
    dataRegistro,
    estrategia,
    doseNome,
    isAplicadoExterior,
    isAplicacaoEquivalente,
    isCovid,
    nomeImunobiologico,
  } = props
  const fabricante = getFabricanteSiglaOuNome(siglaFabricante, nomeFabricante)

  return (
    <Grid
      gap={0}
      gapVertical={1}
      style={css`
        padding: 1rem;
        width: 36rem;
        word-wrap: break-word;
      `}
    >
      {isCovid && (
        <Cell size={5}>
          <InfoLabel title='Estratégia' placeholder='-'>
            {estrategia?.capitalize()}
          </InfoLabel>
        </Cell>
      )}
      {isAplicacaoEquivalente && (
        <Cell size={isCovid ? 7 : 5}>
          <InfoLabel title='Dose aplicada'>{`${nomeImunobiologico?.capitalize() || ' - '} - ${
            doseNome?.capitalize() || ' - '
          }`}</InfoLabel>
        </Cell>
      )}
      <Cell size={isCovid && isAplicacaoEquivalente ? 12 : 7}>
        <InfoLabel title='Lote/Fabricante'>
          {`${lote?.capitalize() || ' - '}/${siglaFabricante ? fabricante : fabricante.capitalize() || '-'}`}
        </InfoLabel>
      </Cell>
      {isCovid && (
        <Cell size={12}>
          <InfoLabel title='Aplicado no exterior' placeholder='-'>
            {isAplicadoExterior ? 'Sim' : 'Não'}
          </InfoLabel>
        </Cell>
      )}
      <Cell size={12}>
        <InfoLabel title='Observações' placeholder='-'>
          {observacoes}
        </InfoLabel>
      </Cell>
      <Cell size={12}>
        <InfoLabel title='Registrado por'>{`${nomeProfissional} (${cbo})`}</InfoLabel>
        {unidadeSaude}
      </Cell>
      <Cell size={8}>
        <Text component='b'>
          Aplicação registrada em
          <DateTime
            value={dataRegistro}
            style={css`
              font-weight: bold;
            `}
            format=' DD/MM/YYYY [às] HH:mm'
          />
        </Text>
      </Cell>
      <Cell size={4}>
        <Tag type='normal' icon='undo'>
          Transcrição de caderneta
        </Tag>
      </Cell>
    </Grid>
  )
}

const createStyles = (theme: Theme) => ({
  panel: {
    margin: '0 0.3rem',
    borderWidth: '0 0.063rem 0.063rem',
    borderStyle: 'solid',
    borderColor: theme.pallete.gray.c60,
    position: 'relative',
    top: '-0.6rem',
  } as CSSProperties,
  accordionButton: {
    display: 'flex',
    padding: '0.5rem',
    border: `0.063rem solid ${theme.pallete.gray.c60}`,
    borderBlockEnd: `0.063rem solid ${theme.pallete.gray.c80}`,
  } as CSSProperties,
})
